import type { ReactNode } from 'react';

import {
  AngleLeftValve,
  AngleRightValve,
  BackPressureRegulatorValve,
  BlockValve,
  IcadControlValve,
  LinearValveGauge,
  MotorizedControlValve,
  PneumaticControlValve,
  PressureRegulatorValve,
  RegulatorDualSolenoidControlValve,
  RegulatorSolenoidControlValve,
  SolenoidValve,
  ThreeWayValve,
  TwoStepSolenoidValve,
  type ValveVariant,
} from '@crossnokaye/ux-animatics';

import { AnimaticValveConfigKind } from '@repo/sdk/models/components';

import { convertPercentToDecimal, getRefValue } from '@/features/equipment-browser/utils';

import { AnimaticConfigProps } from './types';
import { ScaledAnimatic } from '../scaled-animatic';

const mapSdkVariantToUx = (v: string | undefined): ValveVariant | undefined => {
  if (v === 'A-B' || v === 'B-C' || v === 'A-C' || v === 'default') {
    return v;
  }
  return undefined;
};

export const AnimaticValve = ({ config, state, className }: AnimaticConfigProps) => {
  const valve = config.specificAnimaticConfig?.valve;
  const gaugeRef = valve?.gaugeRef;
  const gaugeValue = gaugeRef ? convertPercentToDecimal(getRefValue(gaugeRef)) : undefined;
  const variant = mapSdkVariantToUx(valve?.variant);

  const wrap = (node: ReactNode) => (
    <ScaledAnimatic className={className} scaleX={1} scaleY={1}>
      {node}
    </ScaledAnimatic>
  );

  if (!valve?.kind) {
    return wrap(<ThreeWayValve state={state} variant={variant} />);
  }

  switch (valve.kind) {
    case AnimaticValveConfigKind.AngleLeft: {
      return wrap(<AngleLeftValve state={state} />);
    }
    case AnimaticValveConfigKind.AngleRight: {
      return wrap(<AngleRightValve state={state} />);
    }
    case AnimaticValveConfigKind.BackPressureRegulator: {
      return wrap(<BackPressureRegulatorValve state={state} />);
    }
    case AnimaticValveConfigKind.Block: {
      return wrap(<BlockValve state={state} variant={variant} />);
    }
    case AnimaticValveConfigKind.IcadControl: {
      return wrap(<IcadControlValve gaugeValue={gaugeValue} state={state} />);
    }
    case AnimaticValveConfigKind.LinearGauge: {
      return wrap(<LinearValveGauge gaugeValue={gaugeValue ?? undefined} state={state} />);
    }
    case AnimaticValveConfigKind.MotorizedControl: {
      return wrap(<MotorizedControlValve gaugeValue={gaugeValue} state={state} />);
    }
    case AnimaticValveConfigKind.PneumaticControl: {
      return wrap(<PneumaticControlValve state={state} />);
    }
    case AnimaticValveConfigKind.PressureRegulator: {
      return wrap(<PressureRegulatorValve state={state} />);
    }
    case AnimaticValveConfigKind.RegulatorDualSolenoidControl: {
      return wrap(<RegulatorDualSolenoidControlValve state={state} />);
    }
    case AnimaticValveConfigKind.RegulatorSolenoidControl: {
      return wrap(<RegulatorSolenoidControlValve state={state} />);
    }
    case AnimaticValveConfigKind.Solenoid: {
      return wrap(<SolenoidValve state={state} />);
    }
    case AnimaticValveConfigKind.ThreeWay: {
      return wrap(<ThreeWayValve state={state} variant={variant} />);
    }
    case AnimaticValveConfigKind.TwoStepSolenoid: {
      return wrap(<TwoStepSolenoidValve state={state} variant={variant} />);
    }
    default: {
      return wrap(<ThreeWayValve state={state} variant={variant} />);
    }
  }
};
